import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAppSelector } from '../../../store/hooks';
import { apiGetLeaderboard, apiGetLeaderboardByExpansion } from '../../../api/userCardApi';
import type { LeaderboardEntry } from '../../../api/userCardApi';
import { apiGetExpansions } from '../../../api/expansionApi';
import type { Expansion } from '../../../types/models/expansion';

const Container = styled.div`
  padding: 24px 16px 70px;
  min-height: calc(100vh - 70px);
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: gold;
  margin: 0 0 4px;
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0 0 12px;
`;

const InfoBanner = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  background-color: rgba(255, 255, 255, 0.03);
  border-left: 3px solid rgba(255, 215, 0, 0.45);
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 20px;
  line-height: 1.5;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RANK_COLORS: Record<number, string> = {
  1: 'gold',
  2: '#C0C0C0',
  3: '#cd7f32',
};

const Row = styled.div<{ $isMe: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background-color: ${({ $isMe }) => ($isMe ? 'rgba(255,215,0,0.06)' : 'rgba(255,255,255,0.03)')};
  border: 1px solid ${({ $isMe }) => ($isMe ? 'rgba(255,215,0,0.25)' : 'rgba(255,255,255,0.07)')};
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.15s;

  &:active { opacity: 0.75; }
`;

const RankNum = styled.div<{ $rank: number }>`
  font-size: 16px;
  font-weight: 900;
  color: ${({ $rank }) => RANK_COLORS[$rank] ?? 'rgba(255,255,255,0.3)'};
  min-width: 32px;
  text-align: center;
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
`;

const Username = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Nickname = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.35);
`;

const MeTag = styled.span`
  font-size: 10px;
  background-color: rgba(255, 215, 0, 0.15);
  color: gold;
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 4px;
  padding: 1px 5px;
  margin-left: 6px;
  font-weight: 600;
`;

const Stats = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const ScoreCount = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #fff;
`;

const ScoreLabel = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const RarityLegend = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const RarityLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.45);
`;

const RarityLegendDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const RarityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const RarityBadge = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 3px;
  border-radius: 9px;
  border: 1.5px solid ${({ $color }) => $color};
  color: ${({ $color }) => $color};
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  vertical-align: middle;
`;

const StatusText = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 32px;
  font-size: 14px;
`;

// ─── Avatar stack ─────────────────────────────────────────────────────────────

const AvatarStack = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-shrink: 0;
`;

const SIZE = 24;
const OFFSET = -Math.round(SIZE * 0.28);

const AvatarCircle = styled.div<{ $first: boolean }>`
  width: ${SIZE}px;
  height: ${SIZE}px;
  border-radius: 50%;
  border: 2px solid #111;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
  margin-left: ${({ $first }) => ($first ? 0 : OFFSET)}px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
  }
`;

const AvatarMore = styled.div`
  width: ${SIZE}px;
  height: ${SIZE}px;
  border-radius: 50%;
  border: 2px solid #111;
  background-color: rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${Math.round(SIZE * 0.32)}px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.45);
  flex-shrink: 0;
  margin-left: ${OFFSET}px;
`;

// ─── Filtro espansione (custom dropdown) ─────────────────────────────────────

const DropdownWrapper = styled.div`
  position: relative;
  margin-bottom: 20px;
`;

const DropdownTrigger = styled.button<{ $open: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.06);
  border: 1px solid ${({ $open }) => $open ? 'rgba(255,215,0,0.6)' : 'rgba(255,255,255,0.14)'};
  border-radius: 9px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  padding: 9px 12px;
  cursor: pointer;
  outline: none;
  transition: border-color 0.18s;
  &:active { opacity: 0.8; }
`;

const TriggerLabel = styled.span`
  flex: 1;
  text-align: left;
`;

const TriggerChevron = styled.span<{ $open: boolean }>`
  font-size: 9px;
  color: rgba(255,255,255,0.4);
  transform: ${({ $open }) => $open ? 'rotate(180deg)' : 'rotate(0)'};
  transition: transform 0.18s;
`;

const TriggerThumb = styled.img`
  width: 22px;
  height: 22px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
`;

const DropdownPanel = styled.div`
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  right: 0;
  max-height: 260px;
  overflow-y: auto;
  background-color: #1c1c1c;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 10px;
  z-index: 100;
  box-shadow: 0 8px 32px rgba(0,0,0,0.7);
  padding: 5px;
`;

const DropdownOption = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 7px;
  cursor: pointer;
  font-size: 13px;
  font-weight: ${({ $active }) => $active ? '600' : '400'};
  color: ${({ $active }) => $active ? '#fff' : 'rgba(255,255,255,0.7)'};
  background-color: ${({ $active }) => $active ? 'rgba(255,215,0,0.1)' : 'transparent'};
  &:active { background-color: rgba(255,255,255,0.08); }
`;

const OptionThumb = styled.img`
  width: 30px;
  height: 30px;
  border-radius: 5px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid rgba(255,255,255,0.1);
`;

const OptionThumbPlaceholder = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 5px;
  background-color: rgba(255,215,0,0.08);
  border: 1px solid rgba(255,215,0,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  flex-shrink: 0;
`;

// ─── ExpansionFilter ──────────────────────────────────────────────────────────

type FilterId = 'global' | number;

const getImgSrc = (path: string | null): string | null => {
  if (!path) return null;
  return path.startsWith('http') ? path : (process.env.REACT_APP_STORAGE_URL ?? '') + path;
};

const ExpansionFilter: React.FC<{
  expansions: Expansion[];
  selectedId: FilterId;
  onChange: (id: FilterId) => void;
}> = ({ expansions, selectedId, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const selectedExp = selectedId !== 'global' ? expansions.find((e) => e.id === selectedId) ?? null : null;
  const triggerImgSrc = selectedExp ? getImgSrc(selectedExp.expansion_path) : null;

  return (
    <DropdownWrapper ref={wrapperRef}>
      <DropdownTrigger $open={isOpen} onClick={() => setIsOpen((o) => !o)}>
        {triggerImgSrc
          ? <TriggerThumb src={triggerImgSrc} alt={selectedExp!.name} />
          : <span style={{ fontSize: 16, lineHeight: 1 }}>🏆</span>}
        <TriggerLabel>{selectedId === 'global' ? 'Classifica Globale' : (selectedExp?.name ?? '...')}</TriggerLabel>
        <TriggerChevron $open={isOpen}>▼</TriggerChevron>
      </DropdownTrigger>

      {isOpen && (
        <DropdownPanel>
          <DropdownOption $active={selectedId === 'global'} onClick={() => { onChange('global'); setIsOpen(false); }}>
            <OptionThumbPlaceholder>🏆</OptionThumbPlaceholder>
            Classifica Globale
          </DropdownOption>
          {expansions.map((exp) => {
            const imgSrc = getImgSrc(exp.expansion_path);
            return (
              <DropdownOption key={exp.id} $active={selectedId === exp.id} onClick={() => { onChange(exp.id); setIsOpen(false); }}>
                {imgSrc
                  ? <OptionThumb src={imgSrc} alt={exp.name} />
                  : <OptionThumbPlaceholder>{exp.name[0]}</OptionThumbPlaceholder>}
                {exp.name}
              </DropdownOption>
            );
          })}
        </DropdownPanel>
      )}
    </DropdownWrapper>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const LeaderboardMobile: React.FC = () => {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [expansions, setExpansions] = useState<Expansion[]>([]);
  const [selectedId, setSelectedId] = useState<FilterId>('global');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cache = useRef(new Map<FilterId, LeaderboardEntry[]>());

  const navigate = useNavigate();
  const currentUsername = useAppSelector((state) => state.auth.user?.username ?? state.auth.userLoggedState?.username ?? null);

  useEffect(() => {
    let isMounted = true;
    Promise.all([
      apiGetLeaderboard().then((res) => Array.isArray(res) ? res : (res?.data ?? [])),
      apiGetExpansions(),
    ])
      .then(([leaderboardData, expansionList]) => {
        if (!isMounted) return;
        cache.current.set('global', leaderboardData);
        setEntries(leaderboardData);
        setExpansions(expansionList);
      })
      .catch(() => { if (isMounted) setError('Errore durante il caricamento.'); })
      .finally(() => { if (isMounted) setIsLoading(false); });
    return () => { isMounted = false; };
  }, []);

  const handleFilterChange = async (id: FilterId) => {
    if (id === selectedId) return;
    setSelectedId(id);
    if (cache.current.has(id)) {
      setEntries(cache.current.get(id)!);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = id === 'global'
        ? await apiGetLeaderboard()
        : await apiGetLeaderboardByExpansion(id as number);
      const data = Array.isArray(res) ? res : (res?.data ?? []);
      cache.current.set(id, data);
      setEntries(data);
    } catch {
      setError('Errore durante il caricamento della classifica.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <Title>Classifica</Title>
      <Subtitle>
        {selectedId === 'global'
          ? 'I migliori collezionisti GWF per valore album'
          : `I migliori collezionisti di ${expansions.find((e) => e.id === selectedId)?.name ?? '...'}`}
      </Subtitle>
      <InfoBanner>
        Classifica basata sul <strong style={{ color: 'rgba(255,255,255,0.65)' }}>valore ponderato</strong> delle carte.
        I doppioni contano meno delle carte singole.
      </InfoBanner>

      {isLoading && <StatusText>Caricamento...</StatusText>}
      {error && <StatusText style={{ color: '#dc3545' }}>{error}</StatusText>}
      {!isLoading && !error && entries.length === 0 && (
        <StatusText>Nessun collezionista ancora.</StatusText>
      )}

      {!isLoading && !error && entries.length > 0 && (
        <>
          <RarityLegend>
            <RarityLegendItem><RarityLegendDot $color="rgba(255,255,255,0.45)" />Common</RarityLegendItem>
            <RarityLegendItem><RarityLegendDot $color="#4da6ff" />Rare</RarityLegendItem>
            <RarityLegendItem><RarityLegendDot $color="#b04df0" />Epic</RarityLegendItem>
            <RarityLegendItem><RarityLegendDot $color="gold" />Legendary</RarityLegendItem>
          </RarityLegend>

          {expansions.length > 0 && (
            <ExpansionFilter
              expansions={expansions}
              selectedId={selectedId}
              onChange={handleFilterChange}
            />
          )}

          <List>
            {entries.map((entry) => {
              const isMe = entry.username === currentUsername;
              return (
                <Row key={entry.username} $isMe={isMe} onClick={() => navigate(`/carte/utente/${entry.username}`)}>
                  <RankNum $rank={entry.rank}>#{entry.rank}</RankNum>
                  {entry.wrestlers?.length > 0 && (
                    <AvatarStack>
                      {entry.wrestlers.slice(0, 4).map((w, idx) => (
                        <AvatarCircle key={w.id} $first={idx === 0}>
                          {w.avatar_path && (
                            <img
                              src={w.avatar_path.startsWith('http') ? w.avatar_path : process.env.REACT_APP_STORAGE_URL + w.avatar_path}
                              alt={w.name}
                              title={w.name}
                            />
                          )}
                        </AvatarCircle>
                      ))}
                      {entry.wrestlers.length > 4 && (
                        <AvatarMore>+{entry.wrestlers.length - 4}</AvatarMore>
                      )}
                    </AvatarStack>
                  )}
                  <Info>
                    <Username>
                      {entry.username}
                      {isMe && <MeTag>Tu</MeTag>}
                    </Username>
                    {entry.nickname_forum && <Nickname>{entry.nickname_forum}</Nickname>}
                  </Info>
                  <Stats>
                    <ScoreCount>{entry.score} pt</ScoreCount>
                    <ScoreLabel>{entry.unique_cards} uniche</ScoreLabel>
                    <RarityRow>
                      {entry.legendary_count > 0 && <RarityBadge $color="gold">{entry.legendary_count}</RarityBadge>}
                      {entry.epic_count > 0 && <RarityBadge $color="#b04df0">{entry.epic_count}</RarityBadge>}
                      {entry.rare_count > 0 && <RarityBadge $color="#4da6ff">{entry.rare_count}</RarityBadge>}
                      {entry.common_count > 0 && <RarityBadge $color="rgba(255,255,255,0.5)">{entry.common_count}</RarityBadge>}
                    </RarityRow>
                  </Stats>
                </Row>
              );
            })}
          </List>
        </>
      )}
    </Container>
  );
};

export default LeaderboardMobile;
